/**
 * Panels 2 and 6 — `Appointment Matrix Table` (§B6) and its AI counterpart (§B9).
 *
 * Left block: each umpire's duties by role, **three columns and no fourth**. Right block: how
 * many matches they have shared with each other umpire, diagonal `×`.
 *
 * §B6's own words are printed under the table and are the most important sentence on the page:
 *
 *   > *"No combined total — On-field, reserve and video duties are deliberately not summed. A
 *   > heavy video load is not interchangeable with on-field appointments, so a single total
 *   > would hide exactly the imbalance you are checking for."*
 *
 * The table cannot print one either way: `UmpireMatrixTable` carries no role total to print.
 * The `Total` at the right-hand end is the **co-appointment** total, which is what the
 * technical page's right-hand block also totals, and it sums one thing rather than three.
 *
 * RUN 81 — `On Field Only` (§B6a) sits straight after the competition tabs on both matrices.
 * It narrows the right-hand block to the pair on the pitch, U1 with U2, and leaves the
 * left-hand block exactly as it was: the question it answers is who has umpired alongside whom
 * on the pitch, and an umpire's reserve and video duties are still his duties. It is a pressed
 * button rather than a third tab bar because it combines with the competition filter instead
 * of choosing between alternatives, and it says what it is doing in the group header and in
 * the foot — a matrix whose counts had fallen without a word on the table would read as the
 * Umpires Managers' distribution having changed.
 *
 * RUN 90 — `Print / PDF` sits at the end of the same head, after On Field Only, because it acts
 * on what those two controls have left on the screen. It prints THIS panel in THIS state: the
 * paper is made out of the panel's own markup, so there is no second view of the matrix that
 * could be filtered differently from the one the Technical Delegate is looking at. The one thing
 * the screen does not already say is which filters produced it — a printed grid with no caption
 * is a grid nobody can file — so the head carries a line (`.matmeta`) that names the competition,
 * the On Field Only state and the matches counted. It is `display: none` on screen, where all
 * three are visible as controls, and the only thing this run adds to the panel that is not the
 * button itself.
 */

import { useRef } from "react";

import { COMPETITION_TABS, Tabs } from "../appointments/Tabs.js";
import { competitionLabel, type CompetitionFilter } from "../appointments/derive.js";
import { cellClass } from "../appointments/useCellFlash.js";
import { heatClass, type UmpireDataset, type UmpireMatrixTable, type UmpirePanelMember } from "./derive.js";
import { printUmpireMatrix } from "./matrixPrint.js";

export interface UmpireMatrixPanelProps {
  dataset: UmpireDataset;
  table: UmpireMatrixTable;
  competition: CompetitionFilter;
  onCompetition: (value: CompetitionFilter) => void;
  /**
   * RUN 81 — §B6a's `On Field Only`, and whether it is pressed.
   *
   * The table arrives already counted either way (`buildUmpireMatrix`'s `together`), so what
   * this panel owns is the button and the two places that say which count is on screen: the
   * group header and the scope sentence in the foot. Each matrix is handed its own value and
   * never a shared one (Part C invariant 2).
   */
  onFieldOnly: boolean;
  onOnFieldOnly: (value: boolean) => void;
  flashing: ReadonlySet<string>;
  /** §B9's evenness note, rendered inside the AI panel's foot. */
  children?: React.ReactNode;
}

function stub(umpire: UmpirePanelMember): React.JSX.Element {
  return (
    <>
      <span className="rolechip">{umpire.panel === "AHF" ? "AHF" : "NEU"}</span>
      {umpire.surname}
      <span className="nat num">{umpire.country}</span>
    </>
  );
}

export function UmpireMatrixPanel({
  dataset,
  table,
  competition,
  onCompetition,
  onFieldOnly,
  onOnFieldOnly,
  flashing,
  children,
}: UmpireMatrixPanelProps): React.JSX.Element {
  const tms = dataset === "tmsActual";
  const shown = table.rows;
  const id = tms ? "umpMatTitle" : "aiMatTitle";
  const prefix = tms ? "a" : "r";

  /* RUN 90 — the two things the press needs a hold of: the panel, which IS the paper, and the
     grid inside it, which is what has to be made to fit. Both are read once, at the press, and
     never while the panel is being drawn: the print dress is put on and taken off the live DOM
     by `printUmpireMatrix` rather than kept in this component's state, because the whole of it
     lives inside one blocking call to `window.print()` and a piece of state that exists only
     between two statements is a piece of state this panel would have to be trusted to clear. */
  const sheetRef = useRef<HTMLElement>(null);
  const gridRef = useRef<HTMLTableElement>(null);

  return (
    <section
      className={`panel ${tms ? "panel--d2" : "panel--ai"}`}
      aria-labelledby={id}
      data-testid={tms ? "tms-matrix" : "ai-matrix"}
      ref={sheetRef}
    >
      <div className="panel__head">
        <h3 className="panel__title" id={id}>
          {tms ? "Appointment Matrix Table" : "AI Drafted Appointment Matrix Table"}
        </h3>
        <span className="panel__note">
          {tms ? "The Umpires Managers' Actual Distribution" : "Your Evenness Yardstick"}
        </span>
        <span className="spacer" />
        <span className="filtlab">Competition</span>
        <Tabs
          label={tms ? "Matrix competition filter" : "AI matrix competition filter"}
          options={COMPETITION_TABS}
          value={competition}
          onChange={onCompetition}
          tint="competition"
        />
        {/* RUN 81 — §B6a. `aria-pressed` carries the state for a screen reader as the ink
            ground does for the eye. The How To link lands on the Umpires Managers' matrix,
            which is the first of the two on the page, so only that button carries the anchor. */}
        <button
          type="button"
          className="pairtoggle"
          aria-pressed={onFieldOnly}
          onClick={() => {
            onOnFieldOnly(!onFieldOnly);
          }}
          data-howto={tms ? "on-field-only" : undefined}
          data-testid={tms ? "tms-matrix-onfield-only" : "ai-matrix-onfield-only"}
          title="Count only the pair on the pitch — U1 with U2 — in Appointed Together With. Duties By Role is unchanged."
        >
          On Field Only
        </button>
        {/* RUN 90 — the press, at the end of the row it acts on. Plain words: it opens the
            browser's own print dialogue, from which the Technical Delegate prints or saves a
            PDF, and it promises no file of its own because this system writes none. */}
        <button
          type="button"
          className="matprintbtn"
          data-testid={tms ? "tms-matrix-print" : "ai-matrix-print"}
          onClick={() => {
            printUmpireMatrix(sheetRef.current, gridRef.current);
          }}
          title="Print this matrix as it is shown — A4 landscape, on one page. Your browser's own print dialogue puts it on paper or saves it as a PDF."
        >
          Print / PDF
        </button>
        {/* RUN 90 — the caption the paper needs and the screen does not. Hidden on screen by
            `.matmeta`, shown by the print block, and built from the same two values that drive
            the table above it, so it cannot describe a view the grid is not in. */}
        <p className="matmeta" data-testid={`${prefix}-matrix-print-meta`}>
          {competitionLabel(competition)} ·{" "}
          {onFieldOnly
            ? "Appointed Together With: on-field pairs only (U1 and U2)"
            : "Appointed Together With: all duties"}{" "}
          · {table.fixtures} {table.fixtures === 1 ? "match" : "matches"}
        </p>
      </div>

      <div className="matwrap">
        <table
          className="mattable"
          ref={gridRef}
          aria-label={
            tms
              ? "Umpire appointment matrix as recorded in this system"
              : "AI drafted umpire appointment matrix"
          }
        >
          <thead>
            <tr className="matgrp">
              <th scope="col">Umpires</th>
              <th scope="col" colSpan={3} className="g-mid">
                Duties By Role
              </th>
              <th scope="col" colSpan={shown.length + 1} className="g-right">
                {onFieldOnly ? "Appointed Together With · On Field Only" : "Appointed Together With"}
              </th>
            </tr>
            <tr>
              <th scope="col" className="c-name">
                Umpire
              </th>
              <th scope="col" className="c-onfield g-mid">
                On-Field
              </th>
              <th scope="col">RU</th>
              <th scope="col">VU</th>
              {shown.map((row, index) => (
                <th
                  key={row.umpire.tmsPersonId}
                  scope="col"
                  className={index === 0 ? "first-right" : undefined}
                  title={`${row.umpire.mediaName} (${row.umpire.country})`}
                >
                  {row.umpire.surname}
                </th>
              ))}
              <th scope="col" className="c-tot">
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {shown.map((row) => {
              const id2 = row.umpire.tmsPersonId;
              return (
                <tr key={id2} data-testid={`${prefix}-matrix-${id2}`}>
                  <td
                    className={`c-name${row.umpire.gender === null ? "" : ` g-${row.umpire.gender}`}`}
                  >
                    {stub(row.umpire)}
                  </td>
                  <td
                    className={cellClass(
                      `c-onfield g-mid${row.onField === 0 ? " c-zero" : ""}`,
                      `${prefix}m:${id2}:on`,
                      flashing,
                    )}
                  >
                    {row.onField}
                  </td>
                  <td
                    className={cellClass(
                      row.reserve === 0 ? "c-zero" : "",
                      `${prefix}m:${id2}:ru`,
                      flashing,
                    )}
                  >
                    {row.reserve}
                  </td>
                  <td
                    className={cellClass(
                      row.video === 0 ? "c-zero" : "",
                      `${prefix}m:${id2}:vu`,
                      flashing,
                    )}
                  >
                    {row.video}
                  </td>

                  {shown.map((other, index) => {
                    const edge = index === 0 ? " first-right" : "";
                    const otherId = other.umpire.tmsPersonId;
                    if (otherId === id2) {
                      return (
                        <td key={otherId} className={`c-diag${edge}`}>
                          ×
                        </td>
                      );
                    }
                    const value = row.co[otherId] ?? 0;
                    return (
                      <td
                        key={otherId}
                        className={cellClass(
                          `${heatClass(value)}${edge}`,
                          `${prefix}m:${id2}:${otherId}`,
                          flashing,
                        )}
                      >
                        {value}
                      </td>
                    );
                  })}

                  <td className={cellClass("c-tot", `${prefix}m:${id2}:cot`, flashing)}>
                    {row.coTotal}
                  </td>
                </tr>
              );
            })}
          </tbody>

          <tfoot>
            <tr>
              <td className="c-name">Total</td>
              <td className="c-onfield g-mid">{table.totals.onField}</td>
              <td>{table.totals.reserve}</td>
              <td>{table.totals.video}</td>
              {shown.map((row, index) => (
                <td
                  key={row.umpire.tmsPersonId}
                  className={index === 0 ? "first-right" : undefined}
                >
                  {table.coColumnTotals[row.umpire.tmsPersonId] ?? 0}
                </td>
              ))}
              <td>{table.totals.coTotal}</td>
            </tr>
          </tfoot>
        </table>

        {shown.length === 0 ? (
          <div className="emptystate">
            <b>No umpires in this view.</b>
            The matrix fills as soon as the roster and the appointments are loaded.
          </div>
        ) : null}
      </div>

      <div className="panel__foot">
        <div className="matlegend" aria-label="Co-appointment heat key">
          <span>Co-Appointment Density</span>
          <span className="hsw">
            <i className="k0" aria-hidden="true" />0
          </span>
          <span className="hsw">
            <i className="k1" aria-hidden="true" />1
          </span>
          <span className="hsw">
            <i className="k2" aria-hidden="true" />2
          </span>
          <span className="hsw">
            <i className="k3" aria-hidden="true" />
            3+
          </span>
        </div>
        {/* RUN 90 — the one sentence in this foot that was loose text is given a span of its
            own. Not a rewording and not a re-styling: `.matfootnote` carries no screen rules at
            all. A print block can take an ELEMENT out of the foot and leave the legend behind;
            it has no way to take out a bare text node, and the legend is the only thing under
            the table that belongs on the paper. */}
        <span className="matfootnote">
          {tms ? (
            <>
              <b>Reading the matrix —</b> the left block counts each umpire&rsquo;s duties per
              role; the right block counts matches shared with each other umpire (diagonal = ×).
            </>
          ) : (
            <>
              <b>Compare with the matrix above —</b> the engine spreads reserve and video duties
              across the whole panel rather than leaning on a few umpires.
            </>
          )}
        </span>{" "}
        <span data-testid={`${prefix}-matrix-scope`}>
          Currently reflecting <b>{table.fixtures}</b>{" "}
          {competition === "all" ? "" : `${competitionLabel(competition)} `}
          {table.fixtures === 1 ? "match" : "matches"}.
          {/* RUN 81 — said in words as well as in the header, because the foot is where this
              panel says what its figures are measured over. */}
          {onFieldOnly ? (
            <>
              {" "}
              <span data-testid={`${prefix}-matrix-together-scope`}>
                <b>Appointed Together With</b> is counting on-field pairs only (U1 and U2) —
                reserve and video appointments are left out of its cells, its totals and its
                shading.
              </span>
            </>
          ) : null}
        </span>
        <div className="rulenote" data-testid={`${prefix}-no-total`}>
          <b>No combined total —</b> On-field, reserve and video duties are deliberately{" "}
          <b>not summed</b>. A heavy video load is not interchangeable with on-field
          appointments, so a single total would hide exactly the imbalance you are checking for.
        </div>
        {children}
      </div>
    </section>
  );
}
